import React, { useState, useEffect } from 'react';
import { getCurrentUser } from '../../services/auth';
import { getActiveEnv, setActiveEnv } from '../../services/env';
import SiteContentCategoryTab from './SiteContentCategoryTab';
import SiteContentProductTab from './SiteContentProductTab';
import SiteContentRecipeEmailTab from './SiteContentRecipeEmailTab';
import SiteContentPagesTab from './SiteContentPagesTab';
import SiteContentGlobalTab from './SiteContentGlobalTab';

const TAB_STORAGE_KEY = 'cpx.sitecontent.tab';
const VALID_TABS = ['pdp', 'pcp', 'recipe'];

export default function SiteContent() {
  const user = getCurrentUser();
  const userRoles = Array.isArray(user?.roles) ? user.roles : (user?.roles || '').split(',').map(r => r.trim());
  const isAllowed = userRoles.includes('Admin') || userRoles.includes('Manager');

  const [tab, setTab] = useState(() => {
    try {
      const saved = localStorage.getItem(TAB_STORAGE_KEY);
      return VALID_TABS.includes(saved) ? saved : 'pdp';
    } catch { return 'pdp'; }
  });
  useEffect(() => {
    try { localStorage.setItem(TAB_STORAGE_KEY, tab); } catch {}
  }, [tab]);

  // Active environment the admin edits (BC metafields via bc-proxy + env-scoped
  // pdp_defaults in Neon). Changing it remounts the tabs so they re-fetch from
  // the chosen store. Selecting PROD opens a confirm popover first — those edits
  // hit the live customer-facing store.
  const [env, setEnvState] = useState(getActiveEnv);
  const [confirmTo, setConfirmTo] = useState(null);
  const isProd = env === 'prod';
  const applyEnv = (next) => { setActiveEnv(next); setEnvState(next); setConfirmTo(null); };
  const changeEnv = (next) => {
    if (next === env) return;
    if (next === 'prod') { setConfirmTo(next); return; } // gate prod behind the popover
    applyEnv(next);
  };

  if (!isAllowed) {
    return (
      <div style={{ padding: 24 }}>
        <h1 style={{ color: 'var(--navy)', fontWeight: 700 }}>Site Content</h1>
        <p style={{ color: 'var(--gray-500)' }}>You need Admin or Manager access to view this page.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h1 style={{ color: 'var(--navy)', fontWeight: 700, margin: 0 }}>Site Content</h1>
          <p style={{ color: 'var(--gray-500)', margin: '4px 0 0', fontSize: 13 }}>
            Storefront content overrides. PDP tab controls product pages (images, option badges, tags); PCP tab controls category pages.
          </p>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>
          {isProd && <span style={{ color: '#c0392b', fontWeight: 700 }}>● EDITING PRODUCTION</span>}
          Environment
          <select
            value={env}
            onChange={(e) => changeEnv(e.target.value)}
            style={{
              padding: '6px 10px',
              fontSize: 13,
              fontWeight: 600,
              border: isProd ? '1px solid #c0392b' : '1px solid var(--gray-300)',
              borderRadius: 6,
              color: isProd ? '#c0392b' : (env === 'sb1' ? 'var(--orange)' : 'var(--navy)'),
              background: isProd ? '#fdecea' : '#fff',
              cursor: 'pointer',
            }}
          >
            <option value="sb2">sandbox-2</option>
            <option value="sb1">sandbox-1</option>
            <option value="prod">production</option>
          </select>
        </label>
      </div>

      <div style={{ borderBottom: '1px solid var(--gray-200)', margin: '14px 0 18px', display: 'flex', gap: 4 }}>
        <MainTab label="PDP (Product)" active={tab === 'pdp'} onClick={() => setTab('pdp')} />
        <MainTab label="PCP (Category)" active={tab === 'pcp'} onClick={() => setTab('pcp')} />
        <MainTab label="Recipe Email" active={tab === 'recipe'} onClick={() => setTab('recipe')} />
        <MainTab label="Pages (DB)" active={tab === 'pages'} onClick={() => setTab('pages')} />
        <MainTab label="Global (header/footer)" active={tab === 'global'} onClick={() => setTab('global')} />
      </div>

      {tab === 'pcp' && <SiteContentCategoryTab key={env} />}
      {tab === 'pdp' && <SiteContentProductTab key={env} />}
      {tab === 'recipe' && <SiteContentRecipeEmailTab />}
      {tab === 'pages' && <SiteContentPagesTab key={env} />}
      {tab === 'global' && <SiteContentGlobalTab key={env} />}

      {confirmTo === 'prod' && (
        <div
          onClick={() => setConfirmTo(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '80px 20px 20px', zIndex: 1100 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', border: '2px solid #c0392b', borderRadius: 8, padding: 24, width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
          >
            <h2 style={{ margin: '0 0 10px', color: '#c0392b', fontSize: 18, fontWeight: 700 }}>Switch to Production?</h2>
            <p style={{ margin: '0 0 20px', color: 'var(--gray-600)', fontSize: 13, lineHeight: 1.6 }}>
              You're about to edit <strong>production</strong> storefront content. Changes here affect the <strong>live customer-facing store</strong> immediately.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setConfirmTo(null)}
                style={{ padding: '8px 16px', background: 'var(--gray-200)', color: 'var(--navy)', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={() => applyEnv('prod')}
                style={{ padding: '8px 16px', background: '#c0392b', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >
                Edit production
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function MainTab({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '10px 22px',
        fontSize: 14,
        fontWeight: active ? 700 : 500,
        color: active ? 'var(--orange)' : 'var(--gray-500)',
        background: 'none',
        border: 'none',
        borderBottom: active ? '2px solid var(--orange)' : '2px solid transparent',
        cursor: 'pointer',
        marginBottom: -1,
      }}
    >
      {label}
    </button>
  );
}
